import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { billingAPI } from '../api';
import { useAuth } from '../contexts/AuthContext';

// Friendly names for the provider ids stored on a plan.
const PROVIDER_LABELS = {
  claude: 'Claude', openai: 'ChatGPT', gemini: 'Gemini', ollama: 'Ollama',
  groq: 'Groq', deepseek: 'DeepSeek', openrouter: 'OpenRouter'
};

// The card's feature list. Everything a plan does or does not include is
// listed on every card — including the ones it lacks, struck through — because
// a buyer comparing tiers needs to see the difference, not infer it from an
// absent line.
function planFeatures(plan, imageCost) {
  const n = (v) => v.toLocaleString('en-IN');
  const providers = plan.allowedProviders || [];

  return [
    {
      included: true,
      label: plan.monthlyMessages > 0
        ? `${n(plan.monthlyMessages)} messages per month`
        : 'Unlimited messages'
    },
    {
      included: true,
      label: plan.dailyMessages > 0
        ? `Up to ${n(plan.dailyMessages)} messages per day`
        : 'No daily limit'
    },
    {
      included: true,
      label: providers.length === 0
        ? 'All AI models'
        : providers.map(p => PROVIDER_LABELS[p] || p).join(', ') + ' only'
    },
    // Named explicitly rather than as "tools & integrations": people search the
    // page for the thing they actually want.
    {
      included: !!plan.connectorsEnabled,
      label: 'Google Docs, Sheets, Gmail & Calendar'
    },
    { included: !!plan.connectorsEnabled, label: 'Task sheets (FMS)' },
    { included: !!plan.mcpEnabled, label: 'MCP servers' },
    // The cost is spelled out because an image is worth many messages — a buyer
    // reading "500 messages" would otherwise expect 500 images.
    // Listed right after the models, because it is what makes them able to
    // answer about today rather than about their training cut-off.
    { included: !!plan.searchEnabled, label: 'Web search (news, prices, live info)' },
    {
      included: !!plan.imagesEnabled,
      label: imageCost
        ? `Image generation (${imageCost} messages each)`
        : 'Image generation'
    },
    // True on every plan today. Listed anyway so a cheaper tier does not look
    // stripped of things it actually has.
    { included: true, label: 'Knowledge base & file uploads' },
    { included: true, label: 'Voice replies' }
  ];
}

const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

// Plans + paywall. Reached either by choice ("upgrade") or by being bounced
// here from the chat with a 402.
const Subscribe = () => {
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  // Org-wide setting, shown on every card so the message allowance reads right
  const [imageCost, setImageCost] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payingPlanId, setPayingPlanId] = useState('');
  const [paymentUrl, setPaymentUrl] = useState('');
  const [waitingForPayment, setWaitingForPayment] = useState(false);

  const navigate = useNavigate();
  const { signOut } = useAuth();
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [me, { plans: planList, imageMessageCost }] =
        await Promise.all([billingAPI.me(), billingAPI.myPlans()]);
      setImageCost(imageMessageCost || 0);
      setStatus(me);
      setPlans(planList);
      return me;
    } catch {
      setError('Could not load your subscription. Please refresh.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Clear the poll on unmount so a navigation mid-payment doesn't leave a
  // timer firing against an unmounted component.
  useEffect(() => () => clearInterval(pollRef.current), []);

  // Razorpay confirms to our webhook, not to this tab, so after the user pays
  // there is a gap of a few seconds where the account is still expired. Poll
  // until it flips, otherwise the user sees "payment done" and a dead app.
  const startPolling = (linkId) => {
    setWaitingForPayment(true);
    clearInterval(pollRef.current);

    const startedAt = Date.now();
    let tick = 0;
    pollRef.current = setInterval(async () => {
      tick += 1;

      // Every third tick, ask the server to confirm with Razorpay directly.
      // Without this a lost webhook leaves a paying customer on the plans page.
      if (linkId && tick % 3 === 0) {
        await billingAPI.verifyPayment(linkId).catch(() => {});
      }

      const me = await load();
      if (me?.allowed) {
        clearInterval(pollRef.current);
        setWaitingForPayment(false);
        navigate('/chat');
        return;
      }
      // Give up after 5 minutes rather than polling forever on an abandoned
      // payment. The user can still refresh once the webhook lands.
      if (Date.now() - startedAt > 5 * 60 * 1000) {
        clearInterval(pollRef.current);
        setWaitingForPayment(false);
      }
    }, 4000);
  };

  const handleChoosePlan = async (planId) => {
    setError('');
    setPayingPlanId(planId);
    try {
      const res = await billingAPI.checkout(planId);
      setPaymentUrl(res.shortUrl);
      // Popup blockers return null — the visible link below is the fallback.
      window.open(res.shortUrl, '_blank', 'noopener');
      startPolling(res.linkId);
    } catch (e) {
      setError(e.response?.data?.message || 'Could not start checkout. Please try again.');
    } finally {
      setPayingPlanId('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
        <p className="text-gray-400">Loading plans…</p>
      </div>
    );
  }

  const blocked = status && !status.allowed;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black px-4 py-10">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Choose your plan</h1>
          {blocked ? (
            <p className="text-amber-300">
              {status.state === 'trial_expired'
                ? 'Your free trial has ended. Pick a plan to keep using the assistant.'
                : 'Your subscription has expired. Renew to keep using the assistant.'}
            </p>
          ) : (
            <p className="text-gray-400">
              {status?.state === 'trialing' && status.trialEndsAt
                ? `Free trial active until ${formatDate(status.trialEndsAt)} — upgrade any time.`
                : 'Upgrade or change your plan.'}
            </p>
          )}
        </div>

        {status?.usage?.limit > 0 && (
          <p className="text-center text-sm text-gray-500 mb-2">
            {status.usage.used} / {status.usage.limit} messages used this month
            {status.usage.dailyLimit > 0 &&
              ` · ${status.usage.usedToday} / ${status.usage.dailyLimit} today`}
          </p>
        )}

        {/* Credits are the other, separate thing you can buy — say so plainly
            so nobody reads the plans page as the only option. */}
        {status?.allowed && status?.creditPricePaise > 0 && (
          <p className="text-center text-sm text-gray-500 mb-6">
            Already on a plan and just need more messages?{' '}
            <button onClick={() => navigate('/credits')} className="text-blue-400 hover:text-blue-300">
              Buy usage credits
            </button>
          </p>
        )}

        {error && (
          <div className="mb-6 p-3 bg-red-500/10 border border-red-500/30 rounded-lg max-w-lg mx-auto">
            <p className="text-red-400 text-sm text-center">{error}</p>
          </div>
        )}

        {waitingForPayment && (
          <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg max-w-lg mx-auto text-center">
            <p className="text-blue-300 text-sm">
              Waiting for payment confirmation… this page will unlock automatically.
            </p>
            {paymentUrl && (
              <a
                href={paymentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 text-blue-400 hover:text-blue-300 text-sm underline"
              >
                Payment page didn't open? Click here
              </a>
            )}
          </div>
        )}

        {plans.length === 0 ? (
          <p className="text-center text-gray-400">
            No plans are available right now. Please contact support.
          </p>
        ) : (
          <div className="grid gap-6 md:grid-cols-3">
            {plans.map(plan => (
              <div
                key={plan.id}
                className="bg-gray-800/50 backdrop-blur-md rounded-2xl p-6 border border-gray-700/30 shadow-2xl flex flex-col"
              >
                <h2 className="text-xl font-bold text-white mb-1">{plan.name}</h2>
                <p className="text-3xl font-bold text-white mb-1">
                  ₹{plan.priceINR.toLocaleString('en-IN')}
                  <span className="text-sm font-normal text-gray-400"> /month</span>
                </p>

                <ul className="mt-4 mb-6 space-y-2 text-sm flex-1">
                  {planFeatures(plan, imageCost).map((f, i) => (
                    <li
                      key={i}
                      className={`flex items-start gap-2 ${f.included ? 'text-gray-300' : 'text-gray-500'}`}
                    >
                      <span className={f.included ? 'text-teal-400' : 'text-gray-600'}>
                        {f.included ? '✓' : '✕'}
                      </span>
                      <span className={f.included ? '' : 'line-through decoration-gray-600'}>
                        {f.label}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleChoosePlan(plan.id)}
                  disabled={!!payingPlanId}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {payingPlanId === plan.id ? 'Opening checkout…' : 'Choose plan'}
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 text-center space-x-6">
          {!blocked && (
            <button onClick={() => navigate('/chat')} className="text-gray-400 hover:text-gray-200 text-sm">
              Back to chat
            </button>
          )}
          <button onClick={() => { signOut(); navigate('/signin'); }} className="text-gray-500 hover:text-gray-300 text-sm">
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default Subscribe;
